import { useState, useRef, useCallback, useEffect, useId } from 'react';
import type { FocusEvent, MouseEvent, ReactElement } from 'react';
import { createPortal } from 'react-dom';
import DateTimeReact from 'react-datetime';

import { usePositioning, POSITIONING_STRATEGY, POSITIONING_MODE } from '../../_hooks/usePositioning';
import { useOutsideClick } from '../../_hooks/useOutsideClick';
import useScrollClose from '../../_hooks/useScrollClose';

import type { DateTimeEngineFieldProps, DateTimeEngineProps, DateTimeEngineRenderView } from './DateTimeEngine.types';

import '../../../DateTimeInput.css';
import '../DatePicker.css';

const CALENDAR_HEIGHT = 300;

const DateTimeEngine = ({
    strategy = POSITIONING_STRATEGY.AUTO,
    renderField,
    closeOnSelect = true,
    onChange,
    onOpen,
    onClose,
    className,
    ...dateTimeProps
}: DateTimeEngineProps) => {
    const generatedId = useId();
    const anchorId = `skynet-datepicker-${generatedId}`;

    const [isOpen, setIsOpen] = useState(false);

    const inputRef = useRef<HTMLInputElement>(null);
    const pickerRef = useRef<HTMLDivElement>(null);
    const dateCellClickedRef = useRef(false);

    const positioning = usePositioning(inputRef, pickerRef, isOpen, strategy, {
        preferredHeight: CALENDAR_HEIGHT,
        matchWidth: false,
        allowAbsolute: false,
    });

    const handleClose = useCallback(() => setIsOpen(false), []);
    const handleOpen = useCallback(() => setIsOpen(true), []);

    useOutsideClick(isOpen, inputRef, pickerRef, handleClose);
    useScrollClose(isOpen, pickerRef, handleClose);

    useEffect(() => {
        if (!isOpen) return;

        const handleEscape = (e: globalThis.KeyboardEvent) => {
            if (e.key !== 'Escape') return;
            e.preventDefault();
            e.stopPropagation();
            handleClose();
        };

        document.addEventListener('keydown', handleEscape, { capture: true });

        return () => {
            document.removeEventListener('keydown', handleEscape, { capture: true });
        };
    }, [isOpen, handleClose]);

    const handlePickerClickCapture = useCallback((e: MouseEvent<HTMLDivElement>) => {
        const target = e.target;
        if (target instanceof Element && target.closest('td[data-value]') !== null) {
            dateCellClickedRef.current = true;
            queueMicrotask(() => {
                dateCellClickedRef.current = false;
            });
        }
    }, []);

    const handleChange = useCallback(
        (value: Parameters<NonNullable<DateTimeEngineProps['onChange']>>[0]) => {
            onChange?.(value);

            const pickedFromCalendar = dateCellClickedRef.current;
            dateCellClickedRef.current = false;

            if (closeOnSelect && pickedFromCalendar) handleClose();
        },
        [onChange, closeOnSelect, handleClose]
    );

    const renderInput = useCallback(
        (inputProps: DateTimeEngineFieldProps) => {
            const fieldProps: DateTimeEngineFieldProps = {
                ...inputProps,
                id: anchorId,
                onFocus: (e: FocusEvent<HTMLInputElement>) => {
                    inputProps.onFocus?.(e);
                    handleOpen();
                },
                onClick: (e: MouseEvent<HTMLInputElement>) => {
                    inputProps.onClick?.(e);
                    handleOpen();
                },
                onKeyDown: inputProps.onKeyDown,
            };

            return renderField
                ? renderField(fieldProps, inputRef)
                : <input {...fieldProps} ref={inputRef} className="form-control rounded-8" value={inputProps.value ?? ''} />;
        },
        [anchorId, handleOpen, renderField]
    );

    const renderView = useCallback(
        (_mode: string, renderDefault: () => ReactElement) => {
            if (!isOpen) return <></>;

            const { style, usePopover } = positioning;
            const popoverProps = usePopover ? ({ popover: 'manual', anchor: anchorId } as const) : {};

            const picker = (
                <div
                    ref={pickerRef}
                    className="rdtPicker skynet-datepicker-popover m-0"
                    style={style}
                    onClickCapture={handlePickerClickCapture}
                    {...popoverProps}
                >
                    {renderDefault()}
                </div>
            );

            return positioning.mode === POSITIONING_MODE.PORTAL
                ? createPortal(picker, document.body)
                : picker;
        },
        [isOpen, positioning, anchorId, handlePickerClickCapture]
    );

    const wrapperClassName =
        className === undefined || className === '' ? 'skynet-datepicker' : `skynet-datepicker ${className}`;

    return (
        <DateTimeReact
            {...dateTimeProps}
            className={wrapperClassName}
            onChange={handleChange}
            closeOnSelect={closeOnSelect}
            open={isOpen}
            onOpen={(e) => {
                onOpen?.(e);
                handleOpen();
            }}
            onClose={(value) => {
                onClose?.(value);
                handleClose();
            }}
            renderInput={renderInput}
            renderView={renderView as DateTimeEngineRenderView}
        />
    );
};

export default DateTimeEngine;
